/**
 * AddOnRows — the apps a visitor can add beside the featured anchor (APP-ENTITLEMENT-001 Phase 11), one row each:
 * the app, its published price sentences (the ONE price copy the Billing page reads), its trial, and a "try it"
 * link to the ONE signup URL. An app the console offers with no price published says so instead of linking.
 * The site's own app is marked, never moved: catalog order is the order.
 *
 * Renders on the canonical `.comparison-table` (tables.css) — a real table with a caption, because a price list is
 * tabular data a screen reader should read by column.
 *
 * @module @puffin-core/marketing-shell/pricing/AddOnRows
 */

import PropTypes from 'prop-types';
import { attachableEntriesOf, isPriced, publicEntryOf } from '@puffin-core/app-catalog';
import { priceLinesOf, useAccountAppsUiTranslate } from '@puffin-core/account-apps-ui/copy';

import { SectionHeader } from '../components/SectionHeader.jsx';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { SmartLink } from '../links/SmartLink.jsx';
import { signupUrlFor } from '../registries/signupUrl.js';

const ROW_SHAPE = {
  entry: PropTypes.object.isRequired,
  isSiteApp: PropTypes.bool.isRequired,
  anchorAppKey: PropTypes.string.isRequired,
  fromOrigin: PropTypes.string,
  formatPrice: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tApps: PropTypes.func.isRequired,
  onOutboundClick: PropTypes.func,
};

function AddOnAction({ entry, anchorAppKey, fromOrigin, t, onOutboundClick }) {
  if (!isPriced(entry)) {
    return <span className="text-muted">{t('pricing.notYetOrderable')}</span>;
  }
  return (
    <SmartLink
      href={signupUrlFor({ anchor: anchorAppKey, app: entry.appKey, fromOrigin })}
      className="btn btn-marketing-secondary"
      onOutboundClick={onOutboundClick}
    >
      {t('pricing.startTrialOf', { app: entry.label })}
    </SmartLink>
  );
}

AddOnAction.propTypes = {
  entry: ROW_SHAPE.entry,
  anchorAppKey: ROW_SHAPE.anchorAppKey,
  fromOrigin: ROW_SHAPE.fromOrigin,
  t: ROW_SHAPE.t,
  onOutboundClick: ROW_SHAPE.onOutboundClick,
};

function AddOnRow({
  entry,
  isSiteApp,
  anchorAppKey,
  fromOrigin,
  formatPrice,
  t,
  tApps,
  onOutboundClick,
}) {
  return (
    <tr data-testid={`addon-row-${entry.appKey}`} data-site-app={isSiteApp ? 'true' : undefined}>
      <th scope="row">{isSiteApp ? <strong>{entry.label}</strong> : entry.label}</th>
      <td>
        {priceLinesOf({ entry, t: tApps, formatPrice }).map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </td>
      <td>{t('pricing.trialDays', { days: String(entry.trialDays) })}</td>
      <td>
        <AddOnAction
          entry={entry}
          anchorAppKey={anchorAppKey}
          fromOrigin={fromOrigin}
          t={t}
          onOutboundClick={onOutboundClick}
        />
      </td>
    </tr>
  );
}

AddOnRow.propTypes = ROW_SHAPE;

function AddOnTable({ entries, caption, siteAppKey, rowProps, t }) {
  return (
    <table className="comparison-table" data-testid="addon-rows">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{t('pricing.columnApp')}</th>
          <th scope="col">{t('pricing.columnPrice')}</th>
          <th scope="col">{t('pricing.columnTrial')}</th>
          <th scope="col">{t('pricing.columnAction')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <AddOnRow
            key={entry.appKey}
            entry={entry}
            isSiteApp={entry.appKey === siteAppKey}
            {...rowProps}
          />
        ))}
      </tbody>
    </table>
  );
}

AddOnTable.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.object).isRequired,
  caption: PropTypes.string.isRequired,
  siteAppKey: PropTypes.string.isRequired,
  rowProps: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

/**
 * @param {Object} props
 * @param {import('./pricingRules.js').PublicCatalog} props.catalog
 * @param {string} props.anchorAppKey - the featured anchor the rows attach to.
 * @param {string} props.siteAppKey
 * @param {string} [props.fromOrigin]
 * @param {(cents: number) => string} props.formatPrice
 * @param {Function} [props.t]
 * @param {Function} [props.onOutboundClick]
 * @param {string} [props.id='add-ons']
 * @returns {React.JSX.Element|null} nothing when the anchor offers no app beside it.
 */
export function AddOnRows({
  catalog,
  anchorAppKey,
  siteAppKey,
  fromOrigin,
  formatPrice,
  t: hostT,
  onOutboundClick,
  id = 'add-ons',
}) {
  const t = useMarketingShellTranslate(hostT);
  const tApps = useAccountAppsUiTranslate(hostT);
  const entries = attachableEntriesOf(catalog, anchorAppKey);
  if (entries.length === 0) return null;
  const anchorLabel = publicEntryOf(catalog, anchorAppKey).label;
  return (
    <section className="section-marketing" id={id} aria-labelledby={`${id}-heading`}>
      <SectionHeader
        heading={t('pricing.addOnsHeading')}
        subheading={t('pricing.addOnsSubheading')}
        headingId={`${id}-heading`}
      />
      <div className="comparison-table-wrapper scroll-affordance">
        <AddOnTable
          entries={entries}
          caption={t('pricing.addOnsCaption', { anchor: anchorLabel })}
          siteAppKey={siteAppKey}
          rowProps={{ anchorAppKey, fromOrigin, formatPrice, t, tApps, onOutboundClick }}
          t={t}
        />
      </div>
    </section>
  );
}

AddOnRows.propTypes = {
  catalog: PropTypes.shape({ apps: PropTypes.arrayOf(PropTypes.object).isRequired }).isRequired,
  anchorAppKey: PropTypes.string.isRequired,
  siteAppKey: PropTypes.string.isRequired,
  fromOrigin: PropTypes.string,
  formatPrice: PropTypes.func.isRequired,
  t: PropTypes.func,
  onOutboundClick: PropTypes.func,
  id: PropTypes.string,
};

export default AddOnRows;
